import React, { useState, useContext, useCallback, useEffect } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Video, Users, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

// Zoom Meeting SDK Web - Client View (fullscreen, se ve como app de Zoom nativa)
// Cargado desde CDN para NO impactar el bundle size.
const ZOOM_SDK_VERSION = '3.13.1';

// Scripts y CSS a cargar en orden. Client View necesita los vendors + el build de client view
// + CSS específicos. IMPORTANTE: es zoom-meeting-X.min.js (NO zoom-meeting-embedded).
const ZOOM_STYLESHEETS = [
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/css/bootstrap.css`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/css/react-select.css`,
];
const ZOOM_SDK_SCRIPTS = [
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/react.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/react-dom.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/redux.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/redux-thunk.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/lodash.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/zoom-meeting-${ZOOM_SDK_VERSION}.min.js`,
];

// Defaults: la reunión de prueba que el admin configuró
const DEFAULT_MEETING_ID = '87539655820';

// Carga de un CSS
const loadStylesheet = (href) => new Promise((resolve) => {
  if (document.querySelector(`link[href="${href}"]`)) return resolve();
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.onload = () => resolve();
  link.onerror = () => resolve(); // no fatal si un CSS no carga
  document.head.appendChild(link);
});

// Carga secuencial de un script
const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    if (existing.dataset.loaded === 'true') return resolve();
    existing.addEventListener('load', () => resolve());
    existing.addEventListener('error', () => reject(new Error(`No se pudo cargar: ${src}`)));
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.async = false;
  script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
  script.onerror = () => reject(new Error(`No se pudo cargar: ${src}`));
  document.head.appendChild(script);
});

let _zoomSdkLoadPromise = null;
const loadZoomClientSDK = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.ZoomMtg) return Promise.resolve(window.ZoomMtg);
  if (_zoomSdkLoadPromise) return _zoomSdkLoadPromise;

  _zoomSdkLoadPromise = (async () => {
    // CSS en paralelo (no bloqueante)
    ZOOM_STYLESHEETS.forEach(loadStylesheet);
    // Scripts secuenciales (orden importa)
    for (const src of ZOOM_SDK_SCRIPTS) {
      // eslint-disable-next-line no-await-in-loop
      await loadScript(src);
    }
    if (!window.ZoomMtg) {
      throw new Error('Zoom Client SDK cargó pero window.ZoomMtg no está disponible');
    }
    // Configuración requerida por Client View
    window.ZoomMtg.setZoomJSLib(`https://source.zoom.us/${ZOOM_SDK_VERSION}/lib`, '/av');
    window.ZoomMtg.preLoadWasm();
    window.ZoomMtg.prepareWebSDK();
    // Lenguaje
    try { window.ZoomMtg.i18n.load('es-ES'); } catch (_e) {}
    try { window.ZoomMtg.i18n.reload('es-ES'); } catch (_e) {}
    return window.ZoomMtg;
  })();

  return _zoomSdkLoadPromise;
};

// Mostrar/ocultar el DOM que Zoom inyecta al body (#zmmtg-root)
const showZoomRoot = () => {
  const el = document.getElementById('zmmtg-root');
  if (el) {
    el.style.display = 'block';
    el.style.position = 'fixed';
    el.style.inset = '0';
    el.style.zIndex = '9999';
  }
};
const hideZoomRoot = () => {
  const el = document.getElementById('zmmtg-root');
  if (el) el.style.display = 'none';
};

const ZoomSDKSandbox = () => {
  const { API, user } = useContext(AuthContext);

  const [meetingId, setMeetingId] = useState(DEFAULT_MEETING_ID);
  const [userName, setUserName] = useState(
    user ? `${user.nombre || ''} ${user.apellido || ''}`.trim() : 'Admin'
  );
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [reactivando, setReactivando] = useState(false);
  const [resultadoReactivacion, setResultadoReactivacion] = useState(null);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      try {
        if (window.ZoomMtg) window.ZoomMtg.leaveMeeting({});
      } catch (_e) {}
      hideZoomRoot();
    };
  }, []);

  const entrarASala = useCallback(async (role) => {
    setErrorMsg(null);
    setLoading(true);
    setActiveRole(role);

    try {
      // 1) Cargar Client View SDK
      const ZoomMtg = await loadZoomClientSDK();

      // 2) Pedir signature + password + zak al backend
      const token = localStorage.getItem('token');
      const sigResp = await axios.post(
        `${API}/zoom-sdk-test/signature`,
        { meeting_number: meetingId, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { signature, sdk_key, zak, password } = sigResp.data;

      if (!signature || !sdk_key) {
        throw new Error('El backend no devolvió signature o sdk_key');
      }

      // 3) Mostrar #zmmtg-root antes de init
      showZoomRoot();

      // 4) Init + Join (Client View usa callbacks, no promesas)
      await new Promise((resolve, reject) => {
        ZoomMtg.init({
          leaveUrl: `${window.location.origin}/app/zoom-sdk-sandbox`,
          patchJsMedia: true,
          isSupportAV: true,
          success: () => {
            ZoomMtg.join({
              signature,
              sdkKey: sdk_key,
              meetingNumber: meetingId,
              passWord: password || '',
              userName: userName || 'Participante',
              zak: role === 1 ? (zak || undefined) : undefined,
              success: () => {
                setJoined(true);
                toast.success(role === 1 ? 'Entraste como Anfitrión' : 'Entraste como Estudiante');
                resolve();
              },
              error: (err) => {
                console.error('Join error:', err);
                reject(new Error(err?.reason || err?.errorMessage || 'Error al unirse'));
              },
            });
          },
          error: (err) => {
            console.error('Init error:', err);
            reject(new Error(err?.reason || err?.errorMessage || 'Error al inicializar SDK'));
          },
        });
      });
    } catch (err) {
      console.error('Error entrando al sandbox Zoom:', err);
      const msg = err?.message || err?.reason || 'Error desconocido';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('No se pudo unir a la reunión');
      setActiveRole(null);
      hideZoomRoot();
    } finally {
      setLoading(false);
    }
  }, [API, meetingId, userName]);

  const salirDeSala = useCallback(() => {
    try {
      if (window.ZoomMtg) window.ZoomMtg.leaveMeeting({});
    } catch (_e) {}
    hideZoomRoot();
    setJoined(false);
    setActiveRole(null);
    toast.info('Saliste de la sala');
  }, []);

  const reactivarClasesPiloto = useCallback(async () => {
    setReactivando(true);
    setResultadoReactivacion(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/admin/reactivar-sdk-clases-piloto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResultadoReactivacion(resp.data);
      toast.success(`✅ ${resp.data.total} clases re-activadas con SDK Embebido`);
    } catch (err) {
      console.error('Error reactivando:', err);
      toast.error(err?.response?.data?.detail || 'Error al re-activar clases');
    } finally {
      setReactivando(false);
    }
  }, [API]);

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="zoom-sdk-sandbox">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <Video className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Zoom SDK Sandbox — Client View</h1>
          <p className="text-sm text-gray-500">
            Zoom fullscreen embebido (v{ZOOM_SDK_VERSION}). Se ve igual que la app nativa de Zoom.
            No afecta el flujo actual de clases.
          </p>
        </div>
      </div>

      {!joined && (
        <Card data-testid="zoom-sandbox-prejoin">
          <CardHeader>
            <CardTitle className="text-lg">Configuración de la reunión</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="meeting-id">Meeting ID</Label>
              <Input
                id="meeting-id"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value.replace(/\s/g, ''))}
                placeholder="87539655820"
                data-testid="zoom-sandbox-meeting-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                El passcode se obtiene automáticamente desde la API de Zoom.
              </p>
            </div>

            <div>
              <Label htmlFor="user-name">Tu nombre en la reunión</Label>
              <Input
                id="user-name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Nombre visible para los demás"
                data-testid="zoom-sandbox-name-input"
              />
            </div>

            {errorMsg && (
              <div
                className="bg-red-50 border border-red-300 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2"
                data-testid="zoom-sandbox-error"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Error al unirse:</strong> {errorMsg}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Button
                onClick={() => entrarASala(1)}
                disabled={loading || !meetingId || !userName.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white py-6 text-base"
                data-testid="zoom-sandbox-host-btn"
              >
                {loading && activeRole === 1 ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Video className="w-5 h-5 mr-2" />
                )}
                Entrar como Anfitrión
              </Button>

              <Button
                onClick={() => entrarASala(0)}
                disabled={loading || !meetingId || !userName.trim()}
                variant="outline"
                className="border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 py-6 text-base"
                data-testid="zoom-sandbox-student-btn"
              >
                {loading && activeRole === 0 ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Users className="w-5 h-5 mr-2" />
                )}
                Entrar como Estudiante
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Tips de prueba:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Client View ocupa toda la pantalla con UI nativa de Zoom (gallery, toolbar, chat laterales).</li>
                <li>Abrí dos pestañas o dispositivos: uno como Anfitrión, otro como Estudiante.</li>
                <li>Probá cámara, micrófono, screenshare y chat. El host tiene privilegios completos.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sección: Re-activar SDK Embebido en clases piloto (después de cada deploy) */}
      {!joined && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              Re-activar SDK Embebido en clases piloto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <strong>¿Cuándo usar este botón?</strong> Después de cada <strong>deploy</strong> que hagas,
                las flags <code className="bg-gray-200 px-1 rounded text-xs">usa_sdk_embebido</code> se
                resetean a <code className="bg-gray-200 px-1 rounded text-xs">false</code>. Este botón las vuelve
                a activar en las 5 clases piloto con 1 click.
              </p>
              <p className="text-xs text-gray-500">
                Clases afectadas: Ética Ministerial · Dones Espirituales · Panorámica AT · Consejería Pastoral · Devocionales 3/2026
              </p>
            </div>

            <Button
              onClick={reactivarClasesPiloto}
              disabled={reactivando}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5"
              data-testid="reactivar-sdk-btn"
            >
              {reactivando ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Re-activando...</>
              ) : (
                <><RefreshCw className="w-5 h-5 mr-2" /> Re-activar SDK Embebido en las 5 clases</>
              )}
            </Button>

            {resultadoReactivacion && (
              <div className="bg-white border border-purple-200 rounded-lg p-3 text-sm space-y-1" data-testid="reactivar-sdk-result">
                <p className="font-semibold text-purple-800">
                  ✅ {resultadoReactivacion.total} clases procesadas
                </p>
                <ul className="text-xs space-y-0.5 mt-2">
                  {resultadoReactivacion.clases.map((c) => (
                    <li key={c.id} className="flex items-start gap-1">
                      <span>{c.ok ? '✅' : '❌'}</span>
                      <span>
                        <strong>{c.nombre}</strong>
                        {c.ok && c.zoom_patched && <span className="text-green-600 ml-1"> · Zoom OK</span>}
                        {c.error && <span className="text-red-600 ml-1"> · {c.error}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Botón Salir siempre visible cuando estamos en llamada.
          Zoom inyecta su UI fullscreen al body (#zmmtg-root) — nuestro único overlay es este botón. */}
      {joined && (
        <div className="fixed top-3 right-3 z-[10000] flex items-center gap-2">
          <div className="px-3 py-1 rounded-full bg-black/70 text-white text-xs backdrop-blur-sm">
            {activeRole === 1 ? '👑 Anfitrión' : '🎓 Estudiante'} · {userName}
          </div>
          <Button
            onClick={salirDeSala}
            size="sm"
            variant="destructive"
            data-testid="zoom-sandbox-leave-btn"
          >
            Salir
          </Button>
        </div>
      )}
    </div>
  );
};

export default ZoomSDKSandbox;
